import { action, autorun, Lambda, runInAction, when } from 'mobx';
import { HomePageModel } from './model';
import { BrowseAPIController } from '../../api/browse/controller';
import { EnvModel } from '../../env/model';
import { PrismicController } from '../../prismic/controller';
import { ImageController } from '../../image/controller';
import { ContentModel } from '../../content/model';
import { BugsnagController } from '../../bugsnag/controller';
import { MailchimpModel } from '../../mailchimp/model';
import { PricingAPIController } from '../../api/pricing/controller';
import { AnalyticsController } from '../../analytics/controller';
import { UIController } from '../../ui/controller';
import { createFormField } from '../../../lib/form';
import { PlaylistGroupPlaylistSchema, PlaylistGroupSchema, TrackSchema } from '../../../types/schema';
import { Mode, UICreator } from '../../../types/ui';
import { getAliasedThemePlaylist } from '../../../lib/helpers';
import { APIController } from '../../api/controller';
import { UserModel } from '../../user/model';
import { APIPaginatedResponse, APIResponse } from '../../api/types';

export class HomePageController {
  interval: any;
  disposer: any;
  userContentDisposer: Lambda;

  constructor(
    private model: HomePageModel,
    private env: EnvModel,
    private browse: BrowseAPIController,
    private prismic: PrismicController,
    private image: ImageController,
    private content: ContentModel,
    private bugsnag: BugsnagController,
    private mailchimp: MailchimpModel,
    private api: APIController,
    private analytics: AnalyticsController,
    private ui: UIController,
    private user: UserModel,
  ) {
    this.setupCarousel();
    this.userContentDisposer = () => void 0;
    if (
      !this.env.isServer &&
      (typeof this.model.favouriteTracks === 'undefined' || typeof this.model.favouriteArtists === 'undefined')
    ) {
      this.userContentDisposer = when(
        () => typeof this.user.user === 'object' && this.user.user !== null,
        this.loadUserContent.bind(this),
      );
    }
  }

  dispose = () => {
    if (this.disposer) this.dispose();
  };

  @action
  private setupCarousel = () => {
    if (!this.env.isServer) {
      this.model.query = this.model.heroArtist;
      this.model.cycleCarousel = true;
      this.disposer = autorun(() => {
        this.destroyCarousel();
        if (this.model.cycleCarousel) {
          this.interval = setInterval(this.cycleCarousel, 6000);
        }
      });
    }
  };

  @action
  async loadUserContent() {
    const [favouriteTracks, favouriteArtists] = await Promise.all([
      this.api.playlist.getTracks(this.user.user.identity, this.env.playlistFavourites),
      this.api.artistlist.getArtists(this.user.user.identity, this.env.artistlistFavourites),
    ]);
    this.model.favouriteTracks = favouriteTracks.data;
    this.model.favouriteArtists = favouriteArtists.data;
  }

  @action
  destroyCarousel = () => {
    clearInterval(this.interval);
    this.interval = void 0;
  };

  @action
  cycleCarousel = () => {
    const { artist, artists } = this.model;
    this.model.artist = artist >= artists.length - 1 ? 0 : artist + 1;
    this.model.query = this.model.heroArtist;
  };

  @action
  onEnter = async () => {
    runInAction(() => {
      this.model.loading = true;
      this.model.showSplash = true;
      this.ui.setBreadcrumbs([
        {
          path: '/',
          label: 'Home',
        },
      ]);
    });

    this.hidePricingGrid();
    this.setQuestion(2);

    const documentId = 'home';

    // need the playlist id from global document
    await this.env.ready;

    const carouselPromises = Promise.all<APIPaginatedResponse<TrackSchema[]> | PlaylistGroupSchema>(
      this.content.homepageCarousel.map((item) => this.browse.getTypedPlaylist(item.content_type, item.content_slug)),
    );

    const [content, carousels, featured, ratecards] = await Promise.all([
      this.prismic.getSingle(documentId),
      carouselPromises,
      this.browse.getFeaturedTracks(),
      this.api.pricing.getRateCards(),
    ]);

    await this.ui.setSEO('/');

    const document = content.data;

    const { chartingTracks } = this.content;
    const maybeSetCharted = (item: TrackSchema): TrackSchema => ({
      ...item,
      is_charting: chartingTracks.indexOf(item.identity) !== -1,
    });

    const [firstCarousel, ...additionalCarousels] = carousels.map((carousel) => {
      if ('data' in carousel && Array.isArray(carousel.data)) {
        return carousel.data;
      }
      if ('playlists' in carousel && Array.isArray(carousel.playlists.data)) {
        return carousel.playlists.data;
      }
    });

    runInAction(() => {
      // Up until TS v4.2, TS can't understand the typing here, manually type check and force the type for the mapping
      let justAdded = firstCarousel;
      if (Array.isArray(justAdded) && justAdded.length > 0 && 'tempo' in justAdded[0]) {
        justAdded = (justAdded as TrackSchema[]).map(maybeSetCharted);
      }
      this.model.carousels = [justAdded, ...additionalCarousels];

      this.model.featuredTracks = featured.data;
      this.model.ratecards = ratecards.data;

      this.model.ctaActionLabel = document.cta_action_label;
      this.model.ctaContent = document.cta_content;
      this.model.ctaTitle = document.cta_title;
      this.model.endingSoonTitle = document.ending_soon_title;
      this.model.justAddedTitle = document.just_added_title;
      this.model.homepageThirdCarouselTitle = document.home_third_carousel_title;
      this.model.homepageFourthCarouselTitle = document.home_fourth_carousel_title;
      this.model.homepageFifthCarouselTitle = document.home_fifth_carousel_title;
      this.model.homepageSixthCarouselTitle = document.home_sixth_carousel_title;
      this.model.homepageSeventhCarouselTitle = document.home_seventh_carousel_title;
      this.model.question1 = document.question_1;
      this.model.question1Next = document.question_1_next;
      this.model.question2 = document.question_2;
      this.model.question2Next = document.question_2_next;
      this.model.question3 = document.question_3;
      this.model.question3Next = document.question_3_next;
      this.model.questionSubmit = document.question_submit;
      this.model.section1ActionLabel = document.section_1_action_label;
      this.model.section1ActionTarget = document.section_1_action_target;
      this.model.section1Content = document.section_1_content;
      this.model.section1Title = document.section_1_title;
      this.model.section1Image1 = document.section_1_image_1.url;
      this.model.section1Image2 = document.section_1_image_2.url;
      this.model.section1Image3 = document.section_1_image_3.url;
      this.model.section2Title = document.section_2_title;
      this.model.section2Subtitle1 = document.section_2_subtitle_1;
      this.model.section2Subtitle2 = document.section_2_subtitle_2;
      this.model.section2Content1 = document.section_2_content_1;
      this.model.section2Content2 = document.section_2_content_2;
      this.model.section2Image = document.section_2_image.url;
      this.model.section3Title = document.section_3_title;
      this.model.section3Subtitle1 = document.section_3_subtitle_1;
      this.model.section3Subtitle2 = document.section_3_subtitle_2;
      this.model.section3Content1 = document.section_3_content_1;
      this.model.section3Content2 = document.section_3_content_2;
      this.model.section3Image = document.section_3_image.url;

      this.model.videoId = document.video_id;

      const heroSlice = document.body.find((slice: any) => slice.slice_type === 'hero_artist');
      this.model.artists = ((heroSlice && heroSlice.items) || []).map((artist: any) => ({
        name: artist.name,
        image: artist.image.url,
        slug: artist.slug,
      }));

      this.model.heroSuggestions = ((heroSlice && heroSlice.items) || []).map((artist: any) => ({
        name: artist.name,
        slug: artist.slug,
        images: {
          identity: artist.image.url,
        },
      }));

      // Take these from common
      this.model.genres = this.content.genres;
      this.model.themes = this.content.themes;

      const creatorsSlice = document.body.find((slice: any) => slice.slice_type === 'creators');
      this.model.creators = ((creatorsSlice && creatorsSlice.items) || []).map(
        (creator: any): UICreator => ({
          channel_name: creator.channel_name,
          channel_id: creator.channel_id,
          image: creator.image.url,
          video_title: creator.video.title,
          video_embed: encodeURIComponent(creator.video.html),
        }),
      );

      this.model.creatorsTitle = creatorsSlice.primary.title;
      this.model.creatorsViewCTA = creatorsSlice.primary.view_cta;

      this.model.mailchimpEmail = createFormField({
        name: 'EMAIL',
        label: 'Email address',
        value: '',
        autoComplete: 'email',
      });

      this.model.mailchimpAction = this.mailchimp.launchReminder.action;
      this.model.mailchimpHidden = this.mailchimp.launchReminder.action;

      this.model.benefits = this.content.benefits;

      this.model.pricingChannelId = '';
      this.model.pricingImage = this.content.pricingBackground;
      this.model.pricingBackgroundImage = this.content.pricingBackground;

      this.model.loading = false;
    });

    this.setupCarousel();

    return;
  };

  @action
  reset = () => {
    this.model.resetImpulse = true;
    this.model.selectedGenres = [];
    this.model.selectedTheme = null;
    this.showResults(false);
    setTimeout(
      action(() => (this.model.resetImpulse = false)),
      42,
    );
  };

  @action
  onLeave = async () => {
    if (!this.env.isServer) {
      this.destroyCarousel();
      this.disposer();
      this.userContentDisposer();
    }
  };

  @action
  search = () => {
    this.destroyCarousel();
    this.model.loadingResults = true;
    if (window && window.scrollTo) {
      window.scrollTo(null, 0);
    }
    switch (true) {
      case this.model.question === 2:
        return this.searchPlaylists();

      case this.model.question === 1 && this.model.currentQuery.indexOf('remixes') != -1:
        return this.searchRemixes();

      default:
        return this.searchTracks();
    }
  };

  @action
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private searchTracks = async () => {};

  @action
  private searchPlaylists = async () => {
    if (!this.model.selectedTheme) return;
    this.model.searchState = { query: '' };
    this.model.results = [];

    this.analytics.sendHeroThemeSearch(this.model.selectedTheme);

    const targetPlaylist = getAliasedThemePlaylist(this.model.selectedTheme);

    try {
      const tracks = await this.browse.getPlaylistTracks(targetPlaylist);
      runInAction(() => {
        this.model.results = tracks.data;
        this.showResults(true);
      });
    } catch (e) {
      this.bugsnag.notifyException(e);
    } finally {
      runInAction(() => {
        this.model.loadingResults = false;
      });
    }
  };

  @action
  private searchRemixes = async () => {
    if (this.model.currentQuery.indexOf('remixes') === -1) return;
    this.model.searchState = { query: '' };
    this.model.results = [];

    this.analytics.sendHeroGenreSearch(this.model.selectedGenres[0]);

    try {
      const tracks = await this.browse.getPlaylistTracks(`hookd-hero-genre-remixes`);
      runInAction(() => {
        this.model.results = tracks.data;
        this.showResults(true);
      });
    } catch (e) {
      this.bugsnag.notifyException(e);
    } finally {
      runInAction(() => {
        this.model.loadingResults = false;
      });
    }
  };

  @action
  hideSplash = () => {
    this.model.showSplash = false;
  };

  @action
  showResults = (show = true) => {
    if (show) this.destroyCarousel();
    this.model.showResults = show;
    this.model.resultsVisible = show;
  };

  @action
  updateQuery = (state: any) => {
    this.model.query = state.query || '';
  };

  @action
  clearArtistInput = () => {
    if (this.model.cycleCarousel) {
      this.model.cycleCarousel = false;
      this.model.query = '';
      this.destroyCarousel();
    }
  };

  @action
  updateHits = ({ hits }: { hits: Array<any> }) => {
    if (this.model.question !== 2) {
      this.model.results = hits as Array<TrackSchema>;
      this.model.loadingResults = false;
    }
  };

  @action
  setQuestion = (question: number) => {
    this.model.question = question > 2 ? 0 : question < 0 ? 2 : question;
  };

  @action
  setMode = (mode: Mode) => {
    switch (mode) {
      case Mode.ARTIST:
        return this.setQuestion(1);

      case Mode.GENRE:
        return this.setQuestion(2);

      case Mode.THEME:
      default:
        return this.setQuestion(0);
    }
  };

  @action
  setSelectedTheme = (theme: string) => {
    this.model.selectedTheme = theme;
  };

  @action
  setSelectedArtist = (slug: string) => {
    this.model.artist = this.model.artists.findIndex((artist) => artist.slug === slug);
    this.model.query = this.model.heroArtist;
    this.destroyCarousel();
  };

  @action
  toggleSelectedGenre = (genre: string) => {
    this.model.selectedGenres = [genre];
  };

  @action
  changeMailchimpEmail = (email: string) => {
    this.model.mailchimpEmail.value = email;
  };

  @action
  showPricingGrid = () => {
    this.model.showPricingGrid = true;
  };

  @action
  hidePricingGrid = () => {
    this.model.showPricingGrid = false;
  };

  selectRateType = async (type: string) => {
    const rateType = this.model.ratecardTypes.find((t) => t.value === type);
    if (!rateType) return;

    this.model.ratecardLoading = true;

    try {
      const ratecards = await this.api.pricing.getRateCards(type === 'brand_sponsored');

      runInAction(() => {
        this.model.ratecards = ratecards.data;
        this.model.ratecardType = type;
      });
    } catch (e) {
      this.bugsnag.notifyException(e);
    } finally {
      runInAction(() => {
        this.model.ratecardLoading = false;
      });
    }
  };
}
